import {
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  list,
  deleteObject,
  getBytes,
  getMetadata,
} from "firebase/storage";
import { getAnalytics, logEvent, isSupported } from "firebase/analytics";

// App and storage init live in firebaseApp.ts so that modules this file
// imports (galleryManifest) can reach `storage` without an import cycle.
import { app, storage } from "./firebaseApp";
import { resizeImage, THUMB, WEB } from "./imageResize";
import { CACHE_IMMUTABLE, albumOf, derivedPath } from "./galleryPaths";
import { syncAlbumManifest } from "./galleryManifest";
import { addPhotos, removePhoto, renamePhoto as renameInManifest } from "./galleryManifestOps";

export { storage };

// Initialize Analytics (only in browser environments that support it)
let analytics: ReturnType<typeof getAnalytics> | null = null;
isSupported().then((supported) => {
  if (supported) {
    analytics = getAnalytics(app);
  }
});

// Helper function to log custom events
export const logAnalyticsEvent = (eventName: string, eventParams: Record<string, unknown> = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  }
};

// Helper function to log page views
export const logPageView = (pageName: string, pageLocation: string) => {
  if (analytics) {
    logEvent(analytics, "page_view", {
      page_title: pageName,
      page_location: pageLocation,
    });
  }
};

// Upload tournament cover to Firebase Storage
export const uploadTournamentCover = async (tournamentId: string, file: File): Promise<string> => {
  const timestamp = Date.now();
  const storageRef = ref(storage, `tournament-covers/${tournamentId}_${timestamp}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
};

// Convert base64 to File object (for migration)
export const base64ToFile = async (base64String: string, fileName = "cover.jpg"): Promise<File> => {
  const response = await fetch(base64String);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || "image/jpeg" });
};

// Check if a string is a base64 image
export const isBase64Image = (str: string | undefined | null): boolean => {
  return !!str?.startsWith("data:image");
};

// ============================================
// Tournament Gallery Functions
// ============================================

export interface GalleryPhoto {
  name: string;
  url: string;
  fullPath: string;
}

export interface GalleryFolder {
  name: string;
  path: string;
}

// Helper function to get file extension from a path or name
const getFileExtension = (fileName: string): string => {
  const parts = fileName.split(".");
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
};

// Sanitize file names for Firebase Storage (removes invalid characters only)
const sanitizeFileName = (name: string): string => {
  return name
    .replace(/[#$\[\]*?]/g, "-") // Remove invalid characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .trim();
};

// Upload a single photo to tournament gallery
// Generate and upload both derivatives for a freshly uploaded photo.
// Derivative failures never fail the upload: the clients fall back to the
// original, and the backfill script fills the gap on its next run.
const writeDerivatives = async (originalPath: string, file: File): Promise<void> => {
  await Promise.all(
    [THUMB, WEB].map(async (spec) => {
      try {
        const resized = await resizeImage(file, spec);
        if (!resized) return;
        await uploadBytes(ref(storage, derivedPath(originalPath, spec.kind)), resized.blob, {
          contentType: resized.contentType,
          cacheControl: CACHE_IMMUTABLE,
        });
      } catch (error) {
        console.error(`Derivative "${spec.kind}" failed for ${originalPath}:`, error);
      }
    })
  );
};

// Delete both derivatives. Missing objects are not an error.
const deleteDerivatives = async (originalPath: string): Promise<void> => {
  await Promise.all(
    (["thumb", "web"] as const).map(async (kind) => {
      try {
        await deleteObject(ref(storage, derivedPath(originalPath, kind)));
      } catch {
        /* already gone */
      }
    })
  );
};

export const uploadTournamentPhoto = async (
  tournamentName: string,
  file: File,
  folderName?: string
): Promise<GalleryPhoto> => {
  const timestamp = Date.now();
  const fileName = `${timestamp}-${sanitizeFileName(file.name)}`;

  let path = `tournament-photos/${tournamentName}`;
  if (folderName) {
    path += `/${folderName}`;
  }
  path += `/${fileName}`;

  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || "image/jpeg",
    cacheControl: CACHE_IMMUTABLE,
  });
  const downloadURL = await getDownloadURL(snapshot.ref);

  await writeDerivatives(snapshot.ref.fullPath, file);
  await syncAlbumManifest(tournamentName, (manifest, at) =>
    addPhotos(manifest, [{ path: snapshot.ref.fullPath, timeCreated: at }], at)
  );

  return {
    name: file.name,
    url: downloadURL,
    fullPath: snapshot.ref.fullPath,
  };
};

// Upload multiple photos to tournament gallery
export const uploadTournamentPhotos = async (
  tournamentName: string,
  files: File[],
  folderName?: string,
  onProgress?: (uploaded: number, total: number) => void
): Promise<GalleryPhoto[]> => {
  const photos: GalleryPhoto[] = [];

  for (let i = 0; i < files.length; i++) {
    const photo = await uploadTournamentPhoto(tournamentName, files[i], folderName);
    photos.push(photo);
    onProgress?.(i + 1, files.length);
  }

  return photos;
};

// List all photos in a tournament folder
export const listTournamentPhotos = async (tournamentName: string, folderName?: string): Promise<GalleryPhoto[]> => {
  let path = `tournament-photos/${tournamentName}`;
  if (folderName) {
    path += `/${folderName}`;
  }

  const folderRef = ref(storage, path);

  try {
    const result = await listAll(folderRef);
    const photos: GalleryPhoto[] = [];

    for (const item of result.items) {
      const url = await getDownloadURL(item);
      photos.push({
        name: item.name,
        url,
        fullPath: item.fullPath,
      });
    }

    return photos;
  } catch (error) {
    // Folder doesn't exist yet
    return [];
  }
};

// List photos in a tournament folder with pagination
export const listTournamentPhotosPaginated = async (
  tournamentName: string,
  folderName?: string,
  pageToken?: string,
  pageSize = 40
): Promise<{ photos: GalleryPhoto[]; nextPageToken: string | null }> => {
  let path = `tournament-photos/${tournamentName}`;
  if (folderName) {
    path += `/${folderName}`;
  }

  const folderRef = ref(storage, path);

  try {
    const result = await list(folderRef, { maxResults: pageSize, pageToken });
    const photos = await Promise.all(
      result.items.map(async (item) => {
        const url = await getDownloadURL(item);
        return {
          name: item.name,
          url,
          fullPath: item.fullPath,
        };
      })
    );

    return { photos, nextPageToken: result.nextPageToken ?? null };
  } catch (error) {
    return { photos: [], nextPageToken: null };
  }
};

// Get total count of photos in a folder without loading all photos
export const getTournamentFolderPhotoCount = async (
  tournamentName: string,
  folderName?: string
): Promise<number> => {
  let path = `tournament-photos/${tournamentName}`;
  if (folderName) {
    path += `/${folderName}`;
  }

  const folderRef = ref(storage, path);
  let totalCount = 0;
  let pageToken: string | undefined;

  try {
    do {
      const result = await list(folderRef, { maxResults: 1000, pageToken });
      // Count only actual photos, exclude thumbnails
      totalCount += result.items.filter((item) => !item.name.startsWith("thumbnail")).length;
      pageToken = result.nextPageToken;
    } while (pageToken);
  } catch (error) {
    return 0;
  }

  return totalCount;
};

// List all folders in a tournament gallery
export const listTournamentFolders = async (tournamentName: string): Promise<GalleryFolder[]> => {
  const path = `tournament-photos/${tournamentName}`;
  const folderRef = ref(storage, path);

  try {
    const result = await listAll(folderRef);
    return result.prefixes
      .filter((prefix) => !prefix.name.startsWith("_"))
      .map((prefix) => ({
        name: prefix.name,
        path: prefix.fullPath,
      }));
  } catch (error) {
    // Folder doesn't exist yet
    return [];
  }
};

// Delete a photo from tournament gallery
export const deleteTournamentPhoto = async (fullPath: string): Promise<void> => {
  const photoRef = ref(storage, fullPath);
  await deleteObject(photoRef);
  await deleteDerivatives(fullPath);

  const album = albumOf(fullPath);
  if (album) {
    await syncAlbumManifest(album, (manifest, at) => removePhoto(manifest, fullPath, at));
  }
};

// Rename a photo in the same folder (used for setting thumbnails)
const renameTournamentPhoto = async (
  tournamentName: string,
  photo: GalleryPhoto,
  newName: string,
  folderName?: string
): Promise<GalleryPhoto> => {
  const sourceRef = ref(storage, photo.fullPath);
  const fileBytes = await getBytes(sourceRef);
  
  // Get metadata from source to preserve content type
  let contentType = 'image/jpeg'; // default
  try {
    const sourceMetadata = await getMetadata(sourceRef);
    contentType = sourceMetadata.contentType || 'image/jpeg';
  } catch (error) {
    // If metadata fetch fails, try to infer from file extension
    const extension = getFileExtension(photo.name).toLowerCase();
    if (extension === '.png') contentType = 'image/png';
    else if (extension === '.gif') contentType = 'image/gif';
    else if (extension === '.webp') contentType = 'image/webp';
  }
  
  const extension = getFileExtension(photo.name);

  let targetPath = `tournament-photos/${tournamentName}`;
  if (folderName) {
    targetPath += `/${folderName}`;
  }
  targetPath += `/${newName}${extension}`;

  const targetRef = ref(storage, targetPath);
  
  // Upload with metadata to preserve content type (required by security rules)
  await uploadBytes(targetRef, fileBytes, {
    contentType: contentType,
    cacheControl: CACHE_IMMUTABLE,
  });
  
  await deleteObject(sourceRef);

  // Move both derivatives alongside the original so the cover keeps its
  // optimised versions. A missing derivative is not an error.
  await Promise.all(
    (["thumb", "web"] as const).map(async (kind) => {
      const from = ref(storage, derivedPath(photo.fullPath, kind));
      const to = ref(storage, derivedPath(targetRef.fullPath, kind));
      try {
        const bytes = await getBytes(from);
        await uploadBytes(to, bytes, {
          contentType: "image/webp",
          cacheControl: CACHE_IMMUTABLE,
        });
        await deleteObject(from);
      } catch {
        /* derivative not generated yet */
      }
    })
  );

  await syncAlbumManifest(tournamentName, (manifest, at) =>
    renameInManifest(manifest, photo.fullPath, targetRef.fullPath, at)
  );

  return {
    name: `${newName}${extension}`,
    url: await getDownloadURL(targetRef),
    fullPath: targetRef.fullPath,
  };
};

// Set a photo as folder thumbnail by renaming it to "thumbnail"
// folderName can be undefined for root folder
export const setTournamentFolderThumbnail = async (
  tournamentName: string,
  folderName: string | undefined,
  photo: GalleryPhoto
): Promise<void> => {
  // First, check if there's already a thumbnail and rename it back
  const existingThumbnail = await getTournamentFolderThumbnail(tournamentName, folderName);
  if (existingThumbnail && existingThumbnail.fullPath !== photo.fullPath) {
    // Rename existing thumbnail back to its original name (use timestamp)
    const timestamp = Date.now();
    await renameTournamentPhoto(tournamentName, existingThumbnail, `${timestamp}`, folderName);
  }

  // If the selected photo is already the thumbnail, do nothing
  if (photo.name.startsWith("thumbnail")) {
    return;
  }

  // Rename the selected photo to "thumbnail"
  await renameTournamentPhoto(tournamentName, photo, "thumbnail", folderName);
};

// Delete/remove folder thumbnail by renaming it back
// folderName can be undefined for root folder
export const deleteTournamentFolderThumbnail = async (
  tournamentName: string,
  folderName: string | undefined
): Promise<void> => {
  const thumbnail = await getTournamentFolderThumbnail(tournamentName, folderName);
  if (thumbnail) {
    // Rename thumbnail back to a timestamp-based name
    const timestamp = Date.now();
    await renameTournamentPhoto(tournamentName, thumbnail, `${timestamp}`, folderName);
  }
};

// Get thumbnail for a specific folder by finding the "thumbnail" file
// folderName can be undefined for root folder
const getTournamentFolderThumbnail = async (
  tournamentName: string,
  folderName: string | undefined
): Promise<GalleryPhoto | null> => {
  let path = `tournament-photos/${tournamentName}`;
  if (folderName) {
    path += `/${folderName}`;
  }
  const folderRef = ref(storage, path);
  let pageToken: string | undefined;

  try {
    do {
      const result = await list(folderRef, { maxResults: 1000, pageToken });
      const thumbnailItem = result.items.find((item) => item.name.startsWith("thumbnail"));
      if (thumbnailItem) {
        const url = await getDownloadURL(thumbnailItem);
        return {
          name: thumbnailItem.name,
          url,
          fullPath: thumbnailItem.fullPath,
        };
      }
      pageToken = result.nextPageToken;
    } while (pageToken);
  } catch (error) {
    return null;
  }

  return null;
};

// Get all folder thumbnails including root
export const getTournamentFolderThumbnails = async (
  tournamentName: string
): Promise<{ root: GalleryPhoto | null; folders: Record<string, GalleryPhoto> }> => {
  const thumbnails: Record<string, GalleryPhoto> = {};
  
  // Get root thumbnail
  const rootThumbnail = await getTournamentFolderThumbnail(tournamentName, undefined);
  
  // Get folder thumbnails
  const folders = await listTournamentFolders(tournamentName);
  for (const folder of folders) {
    const thumbnail = await getTournamentFolderThumbnail(tournamentName, folder.name);
    if (thumbnail) {
      thumbnails[folder.name] = thumbnail;
    }
  }

  return { root: rootThumbnail, folders: thumbnails };
};

export const moveTournamentPhoto = async (
  tournamentName: string,
  photo: GalleryPhoto,
  targetFolderName?: string
): Promise<GalleryPhoto> => {
  const sourceRef = ref(storage, photo.fullPath);
  const fileBytes = await getBytes(sourceRef);
  
  // Get metadata from source to preserve content type
  let contentType = 'image/jpeg'; // default
  try {
    const sourceMetadata = await getMetadata(sourceRef);
    contentType = sourceMetadata.contentType || 'image/jpeg';
  } catch (error) {
    // If metadata fetch fails, try to infer from file extension
    const extension = getFileExtension(photo.name).toLowerCase();
    if (extension === '.png') contentType = 'image/png';
    else if (extension === '.gif') contentType = 'image/gif';
    else if (extension === '.webp') contentType = 'image/webp';
  }
  
  const fileName = photo.fullPath.split("/").pop() || photo.name;

  let targetPath = `tournament-photos/${tournamentName}`;
  if (targetFolderName) {
    targetPath += `/${targetFolderName}`;
  }
  targetPath += `/${fileName}`;

  const targetRef = ref(storage, targetPath);
  
  // Upload with metadata to preserve content type (required by security rules)
  await uploadBytes(targetRef, fileBytes, {
    contentType: contentType,
  });
  
  await deleteObject(sourceRef);

  return {
    name: photo.name,
    url: await getDownloadURL(targetRef),
    fullPath: targetRef.fullPath,
  };
};

// Move multiple photos to a target folder
export const moveTournamentPhotos = async (
  tournamentName: string,
  photos: GalleryPhoto[],
  targetFolderName?: string,
  onProgress?: (moved: number, total: number) => void
): Promise<GalleryPhoto[]> => {
  const movedPhotos: GalleryPhoto[] = [];

  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    const movedPhoto = await moveTournamentPhoto(tournamentName, photo, targetFolderName);
    movedPhotos.push(movedPhoto);
    onProgress?.(i + 1, photos.length);
  }

  return movedPhotos;
};

// Get all photos from tournament including all subfolders
export const getAllTournamentPhotos = async (
  tournamentName: string
): Promise<{ folder: string | null; photos: GalleryPhoto[] }[]> => {
  const result: { folder: string | null; photos: GalleryPhoto[] }[] = [];

  // Get photos in root folder
  const rootPhotos = await listTournamentPhotos(tournamentName);
  if (rootPhotos.length > 0) {
    result.push({ folder: null, photos: rootPhotos });
  }

  // Get photos in subfolders
  const folders = await listTournamentFolders(tournamentName);
  for (const folder of folders) {
    const folderPhotos = await listTournamentPhotos(tournamentName, folder.name);
    if (folderPhotos.length > 0) {
      result.push({ folder: folder.name, photos: folderPhotos });
    }
  }

  return result;
};

// Optimized function to get just preview photos and total count for tournament detail page
export const getTournamentPhotosPreview = async (
  tournamentName: string,
  previewCount = 4
): Promise<{ previewPhotos: GalleryPhoto[]; totalCount: number }> => {
  const previewPhotos: GalleryPhoto[] = [];
  let totalCount = 0;

  try {
    // Get root photos with pagination (only first page)
    const rootPath = `tournament-photos/${tournamentName}`;
    const rootRef = ref(storage, rootPath);
    
    // Get first page of root photos (for preview only, don't count yet)
    const rootResult = await list(rootRef, { maxResults: previewCount * 2 });
    
    // Get URLs for preview photos (prefer non-thumbnail files, but include thumbnails if needed)
    const rootPhotoItems = rootResult.items;
    const nonThumbnailItems = rootPhotoItems.filter((item) => !item.name.startsWith("thumbnail"));
    const thumbnailItems = rootPhotoItems.filter((item) => item.name.startsWith("thumbnail"));
    
    // Load non-thumbnail photos first
    const photosToLoad = nonThumbnailItems.slice(0, previewCount);
    for (const item of photosToLoad) {
      const url = await getDownloadURL(item);
      previewPhotos.push({
        name: item.name,
        url,
        fullPath: item.fullPath,
      });
    }

    // If we need more photos, include thumbnails
    if (previewPhotos.length < previewCount && thumbnailItems.length > 0) {
      const remainingNeeded = previewCount - previewPhotos.length;
      for (const item of thumbnailItems.slice(0, remainingNeeded)) {
        const url = await getDownloadURL(item);
        previewPhotos.push({
          name: item.name,
          url,
          fullPath: item.fullPath,
        });
      }
    }

    // If we still need more photos and there are folders, get from folders
    if (previewPhotos.length < previewCount) {
      const folders = await listTournamentFolders(tournamentName);
      for (const folder of folders) {
        if (previewPhotos.length >= previewCount) break;
        
        const folderPath = `tournament-photos/${tournamentName}/${folder.name}`;
        const folderRef = ref(storage, folderPath);
        
        // Get first page of folder photos
        const folderResult = await list(folderRef, { maxResults: previewCount * 2 });
        
        // Prefer non-thumbnail files, but include thumbnails if needed
        const folderNonThumbnailItems = folderResult.items.filter((item) => !item.name.startsWith("thumbnail"));
        const folderThumbnailItems = folderResult.items.filter((item) => item.name.startsWith("thumbnail"));
        
        const remainingNeeded = previewCount - previewPhotos.length;
        
        // Load non-thumbnail photos first
        for (const item of folderNonThumbnailItems.slice(0, remainingNeeded)) {
          const url = await getDownloadURL(item);
          previewPhotos.push({
            name: item.name,
            url,
            fullPath: item.fullPath,
          });
        }
        
        // If still need more, include thumbnails
        if (previewPhotos.length < previewCount && folderThumbnailItems.length > 0) {
          const stillNeeded = previewCount - previewPhotos.length;
          for (const item of folderThumbnailItems.slice(0, stillNeeded)) {
            const url = await getDownloadURL(item);
            previewPhotos.push({
              name: item.name,
              url,
              fullPath: item.fullPath,
            });
          }
        }
      }
    }

    // Get total count from all folders (include thumbnails in count)
    const allFolders = await listTournamentFolders(tournamentName);
    for (const folder of allFolders) {
      const folderPath = `tournament-photos/${tournamentName}/${folder.name}`;
      const folderRef = ref(storage, folderPath);
      try {
        // Count all items including thumbnails
        let pageToken: string | undefined;
        do {
          const result = await list(folderRef, { maxResults: 1000, pageToken });
          totalCount += result.items.length;
          pageToken = result.nextPageToken;
        } while (pageToken);
      } catch (error) {
        // Folder might not exist, continue
      }
    }

    // Count all root photos (include thumbnails in count)
    let rootPageToken: string | undefined;
    do {
      const result = await list(rootRef, { maxResults: 1000, pageToken: rootPageToken });
      totalCount += result.items.length;
      rootPageToken = result.nextPageToken;
    } while (rootPageToken);
  } catch (error) {
    console.error("Error loading gallery preview:", error);
  }

  return { previewPhotos, totalCount };
};

// ============================================
// Event Branding Upload Functions
// ============================================

// Upload event logo to Firebase Storage
// Path: event-branding/{eventId}/logo
export const uploadEventLogo = async (eventId: string, file: File): Promise<string> => {
  const storageRef = ref(storage, `event-branding/${eventId}/logo`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

// Upload event cover image to Firebase Storage
// Path: event-branding/{eventId}/cover
export const uploadEventCover = async (eventId: string, file: File): Promise<string> => {
  const storageRef = ref(storage, `event-branding/${eventId}/cover`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

// Upload a sponsor logo to Firebase Storage
// Path: event-branding/{eventId}/sponsors/{index}
export const uploadEventSponsorLogo = async (
  eventId: string,
  sponsorIndex: number,
  file: File
): Promise<string> => {
  const storageRef = ref(storage, `event-branding/${eventId}/sponsors/${sponsorIndex}`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

export { analytics };
export default app;
